import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelCopies from 'hooks/whitelabel/useWhitelabelCopies';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * Get the line name by its ID from purchase fragments.
 *
 * @param {object} purchase - Purchase object containing departs and returns fragments.
 * @param {string} lineId - Line ID to search for.
 * @returns {string|null} Line name if found, null otherwise.
 */
const getLineNameById = (purchase, lineId) => {
  const departsFragments = purchase?.departs?.fragments || [];
  const returnsFragments = purchase?.returns?.fragments || [];
  const allFragments = [...departsFragments, ...returnsFragments];
  const fragment = allFragments.find((f) => f?.lineId === lineId);
  return fragment?.line?.name || null;
};

/**
 * Get applicable line terms from purchase data based on whitelabel configuration.
 *
 * @param {object} props - purchase, copies, features.
 * @param {object} props.purchase - Purchase object containing trip fragments.
 * @param {object} props.copies - Whitelabel copies configuration containing lineTerms.
 * @param {object} props.features - Whitelabel features configuration.
 * @returns {Array<object>} Array of applicable line terms with lineId, enabled, required, termsUrl, agreementsUrl, and lineName properties.
 */
export const getApplicableLineTerms = ({ purchase, copies, features }) => {
  if (!features?.LINE_SPECIFIC_TERMS_ENABLED) return [];

  const lineTermsConfig = copies?.lineTerms || {};
  const departsFragments = purchase?.departs?.fragments || [];
  const returnsFragments = purchase?.returns?.fragments || [];
  const allFragments = [...departsFragments, ...returnsFragments];

  const lineIds = [...new Set(allFragments.map((f) => f?.lineId).filter(Boolean))];

  return lineIds
    .map((lineId) => {
      const config = lineTermsConfig[lineId];
      if (!config?.enabled) return null;

      return {
        lineId,
        enabled: Boolean(config.enabled),
        required: Boolean(config.required),
        termsUrl: config.termsUrl,
        agreementsUrl: config.agreementsUrl,
        lineName: getLineNameById(purchase, lineId),
      };
    })
    .filter(Boolean);
};

/**
 * Check if all required line terms have been accepted.
 *
 * @param {object} props - applicableTerms, acceptedLineTerms.
 * @param {Array<object>} props.applicableTerms - Array of applicable line terms.
 * @param {object} props.acceptedLineTerms - Object mapping lineId to acceptance status.
 * @returns {boolean} True if all required terms are accepted, false otherwise.
 */
export const areRequiredLineTermsAccepted = ({ applicableTerms, acceptedLineTerms }) =>
  applicableTerms
    .filter((term) => Boolean(term?.required))
    .every((term) => Boolean(acceptedLineTerms?.[term.lineId]));

/**
 * useLineTerms hook.
 *
 * Manage line-specific terms and conditions for the purchase flow.
 *
 * @returns {object} Object containing terms array and areAllRequiredAccepted function.
 * @returns {Array<object>} returns.terms - Array of applicable line terms for the current purchase.
 * @returns {Function} returns.areAllRequiredAccepted - Function to check if all required terms are accepted.
 */
const useLineTerms = () => {
  const purchase = usePurchase();
  const features = useWhitelabelFeatures();
  const copies = useWhitelabelCopies();

  const terms = getApplicableLineTerms({ purchase, copies, features });

  return {
    terms,
    areAllRequiredAccepted: (acceptedLineTerms) =>
      areRequiredLineTermsAccepted({ applicableTerms: terms, acceptedLineTerms }),
  };
};

export default useLineTerms;
